<!-- Standard p-tabs from PrimeNG — matches the pattern used across the app
     (e.g. app-member-detail's detail-tabs). Replaces the hand-rolled tab bar. -->
<p-tabs [value]="activeTab()" (valueChange)="activeTab.set($any($event))" class="sidebar-tabs">
  <p-tablist>
    <p-tab value="info">{{ 'filePreview.sidebar.info' | translate }}</p-tab>
    @if (showSimilar()) {
      <p-tab value="similar">{{ 'filePreview.sidebar.similar' | translate }}</p-tab>
    }
    @if (showComments()) {
      <p-tab value="comments">{{ commentsLabel() | translate }}</p-tab>
    }
    @if (showHistory()) {
      <p-tab value="history">{{ 'itemHistory.label' | translate }}</p-tab>
    }
    @if (showVersions()) {
      <p-tab value="versions">{{ 'fileVersions.label' | translate }}</p-tab>
    }
    @for (tab of extraTabs(); track tab.label) {
      <p-tab [value]="'extra:' + tab.label">{{ tab.label | translate }}</p-tab>
    }
  </p-tablist>

  <p-tabpanels>
    <!-- Info tab: Title / Description / Downloads (context-gated) / Details -->
    <p-tabpanel value="info">
      @if (breadcrumbItems().length > 0) {
        <div class="info-breadcrumb">
          @for (item of breadcrumbItems(); track $index; let isFirst = $first) {
            <div class="info-breadcrumb-line"
                 [class.info-breadcrumb-line--primary]="isFirst && breadcrumbItems().length > 1">
              {{ item }}
            </div>
          }
        </div>
      }

      <!-- Identity zone: Title / Caption / Description — edited via the shared <app-label-field> in
           content mode (article-resources precedent): inline save-on-blur emits a per-locale merge
           patch; the globe popover edits every content locale. Labels are host-rendered
           (global .section-label recipe). Tags close the zone as quiet chips (D5 — no header line;
           read-only English tags, bridge-sourced from AI image analysis). -->
      <div class="info-section info-header">
        @if (showTitle()) {
          <div class="info-field">
            <span class="section-label">{{ 'filePreview.title' | translate }}</span>
            <!-- The three faces sit inside the file preview's full-screen overlay, which inerts the top bar:
                 the missing-translation state's hint says to close the view first (the host declares it). -->
            <app-label-field
              [mode]="'inline'"
              [localeSource]="'content'"
              [value]="item().title ?? {}"
              [canEdit]="effectiveCanEdit()"
              [topBarReachable]="false"
              [unpublishedInLens]="unpublishedInLens()"
              [label]="'filePreview.title' | translate"
              (committed)="onTitlePatched($event)"
              (translated)="onTranslated('title', $event)">
            </app-label-field>
          </div>
        }

        @if (showCaption()) {
          <div class="info-field">
            <span class="section-label">{{ 'filePreview.caption' | translate }}</span>
            <app-label-field
              [mode]="'inline'"
              [localeSource]="'content'"
              [field]="'caption'"
              [value]="item().caption ?? {}"
              [canEdit]="effectiveCanEdit()"
              [topBarReachable]="false"
              [unpublishedInLens]="unpublishedInLens()"
              [label]="'filePreview.caption' | translate"
              [placeholder]="'filePreview.noCaption' | translate"
              (committed)="onCaptionPatched($event)"
              (translated)="onTranslated('caption', $event)">
            </app-label-field>
            @if (captionHint()) {
              <div class="field-hint">{{ captionHint() }}</div>
            }
          </div>
        }

        @if (!compact() && showDescription()) {
          <div class="info-field">
            <span class="section-label">{{ 'filePreview.description' | translate }}</span>
            <app-label-field
              [mode]="'inline'"
              [localeSource]="'content'"
              [field]="'description'"
              [value]="item().description ?? {}"
              [canEdit]="effectiveCanEdit()"
              [topBarReachable]="false"
              [unpublishedInLens]="unpublishedInLens()"
              [label]="'filePreview.description' | translate"
              [placeholder]="'filePreview.noDescription' | translate"
              [multiline]="true"
              (committed)="onDescriptionPatched($event)"
              (translated)="onTranslated('description', $event)">
            </app-label-field>
          </div>
        }

        @if (!compact() && item().tagValues?.length) {
          <app-tags-display [tags]="item().tagValues" [clickable]="false"></app-tags-display>
        }
      </div>

      <!-- Downloads section — context-gated via FilePreviewContext.infoOptions.sections.downloads -->
      @if (!compact() && showDownloads()) {
        <div class="info-section">
          <div class="section-label">{{ 'filePreview.downloads' | translate }}</div>
          <div class="download-options">
            @if ((contentType() === 'image' || contentType() === 'video') && downloadOptions().length > 0) {
              @for (option of downloadOptions(); track option.size) {
                <button
                  class="download-option"
                  (click)="onDownload(option.size)">
                  <span class="size-badge">{{ option.size === 'original' ? 'F' : option.size[0].toUpperCase() }}</span>
                  <div class="option-info">
                    <span class="option-label">{{ option.label | translate }}</span>
                    <span class="option-meta">
                      @if (option.dimensions) {
                        {{ option.dimensions.width }} × {{ option.dimensions.height }}
                      }
                      @if (option.fileSize) {
                        · {{ option.fileSize | formatSize }}
                      }
                      · {{ option.format }}
                    </span>
                  </div>
                  <fa-duotone-icon class="download-icon" [icon]="faDownload"></fa-duotone-icon>
                </button>
              }
            } @else if (contentType() !== 'image') {
              <button class="download-option" (click)="onDownload()">
                <span class="size-badge">
                  <fa-duotone-icon [icon]="faDownload"></fa-duotone-icon>
                </span>
                <div class="option-info">
                  <span class="option-label">{{ 'filePreview.downloadFile' | translate }}</span>
                  <span class="option-meta">
                    @if (item().fileData?.size) {
                      {{ item().fileData!.size | formatSize }}
                    }
                  </span>
                </div>
                <fa-duotone-icon class="download-icon" [icon]="faDownload"></fa-duotone-icon>
              </button>
            }
          </div>
        </div>
      }

      <!-- Details section — visible by default; gated via section visibility -->
      @if (!compact() && showDetails()) {
        <div class="info-section">
          <div class="section-label">{{ 'filePreview.details' | translate }}</div>
          <div class="details-list">
            @if (item().name) {
              <div class="detail-row">
                <span class="detail-label">{{ 'filePreview.fileName' | translate }}</span>
                <span class="detail-value" [title]="item().name">{{ item().name }}</span>
              </div>
            }
            @if (item().fileData?.width && item().fileData?.height) {
              <div class="detail-row">
                <span class="detail-label">{{ 'filePreview.dimensions' | translate }}</span>
                <span class="detail-value">{{ item().fileData!.width }} × {{ item().fileData!.height }} px</span>
              </div>
            }
            @if (item().fileData?.size) {
              <div class="detail-row">
                <span class="detail-label">{{ 'filePreview.size' | translate }}</span>
                <span class="detail-value">{{ item().fileData!.size | formatSize }}</span>
              </div>
            }
            @if (item().created) {
              <div class="detail-row">
                <span class="detail-label">{{ 'filePreview.created' | translate }}</span>
                <span class="detail-value">{{ item().created | displayDate:'medium' }}</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Rights & compliance zone — status rows embedding the license/
           provenance/lock sections; the Check action is section-owned. The lock row alone
           justifies rendering for every File item; per-row flags pass through. A members'
           surface: the zone never mounts for a public visitor (the three fields arrive fenced
           and would read as facts — "None", "Unlocked"). -->
      @if (!compact() && showGovernance()) {
        <app-governance-section
          [item]="item()"
          [showLicense]="showLicense()"
          [showProvenance]="showProvenance()"
          (itemUpdated)="itemUpdated.emit($event)" />
      }

      @if (secondaryAction(); as action) {
        <div class="info-section info-secondary-action">
          <button class="secondary-action-btn" (click)="onSecondaryActionClick()">
            <fa-duotone-icon [icon]="action.icon"></fa-duotone-icon>
            {{ action.label | translate }}
          </button>
        </div>
      }
    </p-tabpanel>

    <!-- Similar items tab: lazy-loads on activation. Backend dispatches by source ItemType.
         Hidden when the context sets sections.similar = false (e.g. Reviews). -->
    @if (showSimilar()) {
    <p-tabpanel value="similar">
      <div class="similar-panel">
        @if (similarLoading()) {
          <div class="similar-loading">
            <p-progressSpinner styleClass="similar-spinner" />
          </div>
        } @else if (similarResults().length === 0) {
          <div class="similar-empty">{{ 'filePreview.sidebar.similarEmpty' | translate }}</div>
        } @else {
          <!-- A click here OPENS the similar item in the preview — there is no selection model, so Space
               (the select key) stays inert and Enter opens; the tiles' selection chrome stays off. -->
          <app-media-masonry
            [items]="similarMosaicItems()"
            viewSize="small"
            [selectable]="false"
            [showBookmark]="true"
            (itemClick)="onSimilarClicked($event)"
            (itemDoubleClick)="onSimilarClicked({ item: $event })" />
        }
      </div>
    </p-tabpanel>
    }

    <!-- Comments tab: universal comment thread anchored on the current item.
         Wrapped in a padded container so the thread + composer aren't flush to
         the tabpanel edge. The Info tab doesn't need this because its own
         `.info-section` blocks carry their own padding. -->
    @if (showComments()) {
      <p-tabpanel value="comments">
        <div class="comments-panel">
          <app-comment-thread [parentId]="commentParentId()" [contextKey]="commentContextKey()"
            [pendingAnchor]="pendingAnchor()"
            [timestampReArm]="videoTimestampReArm()"
            [roundTagFor]="commentRoundTagFor()"
            [activeAnchorId]="activeAnchorId()"
            [canPost]="canPost()"
            [canModerate]="canModerate()"
            [anchorsNavigable]="anchorsNavigable()"
            [focusedCommentUrlId]="pendingCommentFocus()"
            [commentLinkFor]="commentLinkBuilder()"
            (focusHandled)="onCommentFocusHandled()"
            (commentPosted)="onCommentPosted()"
            (commentMutated)="onCommentMutated()"
            (anchorDismissed)="onAnchorDismissed()"
            (composerFocusChange)="onComposerFocusChange($event)"
            (timestampReArmRequested)="onTimestampCaptureRequest()"
            (roundTagClick)="onRoundTagClick($event)"
            (anchorNavigate)="onAnchorNavigate($event)"
            (anchorHoverChange)="onAnchorHoverChange($event)"></app-comment-thread>
        </div>
      </p-tabpanel>
    }

    <!-- History tab: the universal per-item event log.
         Loads on tab ACTIVATION (the Similar/Comments model — an embedded section has no
         expand toggle). Padded container like the comments panel. -->
    @if (showHistory()) {
      <p-tabpanel value="history">
        <div class="history-panel">
          <app-item-history-section [item]="item()" [embedded]="true" [active]="activeTab() === 'history'"/>
        </div>
      </p-tabpanel>
    }

    <!-- Versions tab: NOW-only, gated on version-existence by the HOST (the
         section consumes the same memoized fetch). itemUpdated bubbles the post-promote fresh
         item so the preview refreshes fileData. -->
    @if (showVersions()) {
      <p-tabpanel value="versions">
        <div class="versions-panel">
          <app-file-versions-section [item]="item()" [embedded]="true"
                                     [promoteEnabled]="!versionsReadOnly()"
                                     (itemUpdated)="itemUpdated.emit($event)"/>
        </div>
      </p-tabpanel>
    }

    <!-- Context-provided extra tabs (e.g. pillar-specific sidebars). -->
    @for (tab of extraTabs(); track tab.label) {
      <p-tabpanel [value]="'extra:' + tab.label">
        <ng-container *ngComponentOutlet="tab.component; inputs: tab.inputs ?? {}"></ng-container>
      </p-tabpanel>
    }
  </p-tabpanels>
</p-tabs>
